import React, { useState } from "react";
import { useGetGiftcardTransactionsQuery } from "../../../features/giftcards/giftcardsSlice";
import { GiftcardTransactionDetails } from "./GiftcardTransactionDetails";
import { format } from "date-fns";

export const GiftcardTransactionHistory: React.FC = () => {
  const [page, setPage] = useState(1);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);

  const { data, isLoading, error } = useGetGiftcardTransactionsQuery({
    page,
    limit: 10,
  });

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "text-green-500";
      case "failed":
        return "text-red-500";
      case "processing":
      case "queued":
      case "initiated":
        return "text-yellow-500";
      default:
        return "text-gray-400";
    }
  };

  const formatNGN = (value: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
    }).format(value);
  };

  if (selectedRequestId) {
    return (
      <GiftcardTransactionDetails
        requestId={selectedRequestId}
        onBack={() => setSelectedRequestId(null)}
      />
    );
  }

  return (
    <div className="bg-black px-4 py-5 mt-6 rounded-lg">
      <h3 className="text-primary text-xl font-medium mb-4">
        Gift Card Purchase History
      </h3>

      {isLoading && (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-800 rounded-lg animate-pulse" />
          ))}
        </div>
      )}

      {error && (
        <div className="text-red-500 text-center py-4">
          Failed to load transactions.
        </div>
      )}

      {!isLoading && !error && data?.transactions.length === 0 && (
        <div className="text-gray-400 text-center py-8">
          No gift card purchases yet.
        </div>
      )}

      {!isLoading && !error && data?.transactions && (
        <>
          <div className="space-y-3">
            {data.transactions.map((tx) => (
              <button
                key={tx.request_id}
                onClick={() => setSelectedRequestId(tx.request_id)}
                className="w-full flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors text-left"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium truncate">
                      {tx.brandName}
                    </span>
                    <span className="text-gray-400 text-sm">x{tx.quantity}</span>
                  </div>
                  <p className="text-gray-400 text-sm truncate">{tx.productName}</p>
                  <p className="text-gray-400/70 text-xs mt-1">
                    {format(new Date(tx.created_at), "MMM d, yyyy h:mm a")}
                  </p>
                </div>
                <div className="text-right ml-4">
                  <p className="text-white font-medium">{formatNGN(tx.totalNGN)}</p>
                  <p className={`text-sm ${getStatusColor(tx.status)} capitalize`}>
                    {tx.status}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {/* Pagination */}
          {data.total > data.limit && (
            <div className="flex justify-center items-center gap-4 mt-6">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 bg-gray-800 text-white rounded disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-gray-400">
                Page {page} of {Math.ceil(data.total / data.limit)}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= Math.ceil(data.total / data.limit)}
                className="px-4 py-2 bg-gray-800 text-white rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
